import { QueryClient } from "@tanstack/react-query";

/* The single QueryClient for the app. Mounted once in index.js, INSIDE the
   Redux Provider — Redux keeps owning client state (auth, selected store,
   table-column preferences, purchase-order drafts); this owns server state.

   Every default below is set against what the app ALREADY did, not against
   what the library ships. The v5 defaults would each have changed observable
   behaviour, so the ones that would are turned off explicitly:

   retry — v5 retries a failed query 3 times with backoff. The pages here
     wrap every call in try/catch and toast the API's own message immediately,
     so 3 retries would delay each error toast by seconds and fire the request
     four times. 0 keeps the old "fail once, toast now" timing.

   refetchOnWindowFocus — v5 refetches when the tab regains focus. Nothing in
     this app has ever re-requested on focus; leaving it on would add traffic
     the merchant never asked for and could swap a table out from under a
     half-finished selection.

   refetchOnReconnect — same reasoning, for network reconnects.

   staleTime 30s — the conservative end of the range. It is what removes the
     duplicate requests this migration targets: a remount from navigating
     Products -> Customers -> Products, or two components asking for the same
     lookup in the same tick, now reuse one response. It is deliberately
     short enough that anything a merchant edits elsewhere is re-read within
     half a minute. Reference data that genuinely changes rarely overrides
     this per-query (see LOOKUP_STALE_TIME); nothing caches indefinitely.

   refetchOnMount stays at its default (true-if-stale), which is what keeps
     the "data is fresh when you open the page" behaviour the app has today.

   gcTime 5min is the v5 default, stated here only so it is visible next to
     staleTime: a cache entry outlives its freshness window, so a remount
     shows the previous rows instantly and refetches behind them instead of
     flashing an empty table. */
export const LOOKUP_STALE_TIME = 5 * 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

export default queryClient;
